<template>
  <div class="nav-bar" role="menu" tabindex="0" ref="menu">
    <navigation-menu-header :close-modal="closeModal" />
    <div v-if="promotion" class="promotion-banner">
      <ambassadors-menu-banner v-if="promotion.key === 'ambassadors-menu-banner'" @click="openPromotion"/>
      <div v-else class="image-promotion">
        <img :src="`/images/promotions/${promotion.imgName}`"
             @click="openPromotion" @keyup.enter="openPromotion" tabindex="0" :alt="promotion.description"/>
      </div>
    </div>
    <div class="menu-section first">
      <navigation-menu-item title="תזרים חודשי" v-on:navigate="closeModal" target="sr" icon="coins"/>
      <balance-page-menu-item v-on:navigate="closeModal"/>
      <navigation-menu-item icon="playing-cards-fan"
                            title="הרייזאפ היומי"
                            v-on:navigate="openDailyRoutine" target="home/current"
                            segmentEventSuffix="dr-home">
        <div class="small-circle ri-bold-label" v-if="dailyRoutines.length">{{ dailyRoutines.length }}</div>
      </navigation-menu-item>
    </div>
    <div class="divider"/>
    <div class="menu-section">
      <navigation-menu-dropdown v-if="showMortgagePage || showMortgageResultsPage"
                                title="משכנתא עם רייזאפ" icon="house" segmentEventSuffix="mortgage">
        <navigation-menu-item v-if="showMortgagePage" title="המשכנתא שלי" :small="true"
                              v-on:navigate="closeModal" target="mortgage"
                              segmentEventSuffix="mortgage"/>
        <navigation-menu-item v-if="showMortgageResultsPage" title="כמה משכנתא אני יכול?" :small="true"
                              v-on:navigate="closeModal" target="mortgage-results"
                              segmentEventSuffix="mortgage-results"/>
      </navigation-menu-dropdown>
      <menu-item icon="heart-handshake"
                 title="ליווי כלכלי עם רייזאפ"
                 :on-click="goToAdvisory"
                 show-new-badge
                 :selected="false"/>
      <navigation-menu-dropdown title="חיסכון עם רייזאפ" icon="plant-pot">
        <saving-zone-menu-item v-on:navigate="closeModal"/>
        <balance-money-menu-item v-on:navigate="closeModal" target-path="balance-money"/>
      </navigation-menu-dropdown>
      <navigation-menu-dropdown title="כלים" icon="toolbox">
        <navigation-menu-item title="ההתקדמות שלי" :small="true"
                              v-on:navigate="closeModal" target="my-progress" :disabled="!customerStateIsActive"
                              segmentEventSuffix="MyProgress"/>
        <navigation-menu-item title="כמה עלה לי?" v-on:navigate="closeModal" target="stories" :small="true"/>
        <navigation-menu-item title="מה יקרה אם...?" v-on:navigate="closeModal" target="planner" :small="true" show-beta-badge />
        <plan-ahead-menu-item v-on:navigate="goToPlanAheadPage"/>
      </navigation-menu-dropdown>
    </div>
    <template v-if="hasOffersSection">
      <div class="divider bottom"/>
      <div class="menu-section compact">
        <menu-item v-if="showAigMortgageInsurance"
                   icon="shield-check"
                   title="ביטוח משכנתא ב-AIG"
                   :on-click="goToAigMortgageInsurance"
                   :selected="false"/>
        <navigation-menu-item v-if="!preventPayments" icon="shopping-cart" title="תווי קניה חוסכים לכם בסופר"
                              v-on:navigate="closeModal" :target="vouchersTarget"
                              :disabled="!customerStateIsActive"
                              segmentEventSuffix="SuperDiscount" />
        <navigation-menu-item v-if="showTaxReturnPage" icon="notepad-text"
                              title="מגיע לי החזר מס?"
                              v-on:navigate="closeModal" target="tax-return"
                              segmentEventSuffix="tax-return"/>
        <navigation-menu-item v-if="showIBIInvestmentsPage" icon="hand-coins"
                              title="השקעות עם IBI"
                              v-on:navigate="closeModal" target="ibi-investments"
                              segmentEventSuffix="ibi-investments"/>
        <navigation-menu-item v-if="showExperimentalPages" title="רעיונות בפיתוח" v-on:navigate="closeModal"
                              target="experimental-pages" icon="lightbulb"/>
      </div>
    </template>
    <div class="tiles-grid">
      <menu-tile v-for="tile in menuTiles" v-bind="tile" :key="tile.text" @click="tile.onClick"/>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex';
import PlanAheadIntroductionPopup
  from '@/pages/responsive-pages/authenticated/navigation-pages/plan-ahead/PlanAheadIntroductionPopup';
import MenuTile from '@/base-components/app-header/MenuTile';
import MenuItem from '@/base-components/app-header/MenuItem';
import openAssistant from '@/chat-boot/assistantLauncher';
import pageInitiator from '@/chat-boot/pageInitiator';
import PushNotificationPermissionPopup
  from '@/pages/responsive-pages/authenticated/components/PushNotificationPermissionPopup';
import { isAndroid, isIOS, isNativeApp, getPlatformInfo, getAppVersionNumber } from '@/utils/deviceUtils';
import router, { externalLinkRedirect } from '@/router';
import BalanceMoneyMenuItem from './navigation-menu-items/BalanceMoneyMenuItem';
import BalancePageMenuItem from './navigation-menu-items/BalancePageMenuItem.vue';
import SavingZoneMenuItem from './navigation-menu-items/SavingZoneMenuItem';
import PlanAheadMenuItem from './navigation-menu-items/PlanAheadMenuItem';
import Segment from '../../Segment';
import NavigationMenuItem from './NavigationMenuItem.vue';
import NavigationMenuHeader from './NavigationMenuHeader';
import NavigationMenuDropdown from './NavigationMenuDropdown';
import AmbassadorsMenuBanner from './AmbassadorsMenuBanner';

const HELP_CHAT_MESSAGE = 'יש לי בעיה, אפשר עזרה?';

export default {
  name: 'NavigationMenu',
  components: {
    AmbassadorsMenuBanner,
    MenuTile,
    MenuItem,
    SavingZoneMenuItem,
    BalanceMoneyMenuItem,
    BalancePageMenuItem,
    NavigationMenuItem,
    PlanAheadMenuItem,
    NavigationMenuHeader,
    NavigationMenuDropdown,
  },
  data() {
    return {
      customerHasHeverBenefit: true,
    };
  },
  props: {
    closeModal: {
      type: Function,
      required: true,
    },
  },
  computed: {
    ...mapGetters(
      'featureFlags',
      ['showExperimentalPages',
        'showNewVouchersFlow',
        'eoyMenuBannerSection',
        'preventPayments',
        'showMortgagePage',
        'newAppBanner',
        'newIosAppBanner',
        'showIBIInvestmentsPage',
        'showTaxReturnPage',
        'showMortgageResultsPage',
      ],
    ),
    ...mapGetters('planAhead', ['hasPlans']),
    ...mapGetters('advisory', ['advisoryFlowSharedUTMParams']),
    ...mapGetters('referrals', ['isDuringCompetition']),
    ...mapGetters('subscriptionState', ['hasAccessToCashflow', 'isDormant']),
    ...mapState('applicationState', ['cashflowState']),
    ...mapGetters('applicationState', ['cashflowTypes']),
    ...mapState('dailyRoutines', ['dailyRoutines']),
    ...mapGetters('pushNotifications', ['hasPushTokens']),
    ...mapState('pushNotifications', { pushNotificationsInitialized: 'initialized' }),
    menuTiles() {
      const tiles = [
        {
          text: 'צ׳אט עם הצוות',
          icon: 'chat-bubble',
          onClick: this.openChat,
        },
        {
          text: 'לקהילת הפייסבוק',
          icon: 'diversity',
          onClick: this.goToFacebook,
        },
        {
          text: 'מרכז המידע',
          icon: 'document-search',
          onClick: this.openFaq,
        },
      ];
      if (this.preventPayments) {
        return tiles;
      }
      return [
        ...tiles,
        {
          text: 'להמליץ ולהרוויח',
          icon: 'pages',
          onClick: this.goToAmbassadorsFromNavigationMenu,
        },
      ];
    },
    promotion() {
      if (this.isDuringCompetition) {
        return {
          key: 'ambassadors-competition-banner',
          description: 'תחרות השגרירים — להמליץ ולזכות',
          imgName: 'ambassador_competition_banner.png',
          onClick: () => this.goToAmbassadors(),
        };
      }
      const { appVersion } = getPlatformInfo();
      const versionCheck = getAppVersionNumber(appVersion) >= 1.24;
      if (isNativeApp() && versionCheck && this.hasAccessToCashflow && this.pushNotificationsInitialized && !this.hasPushTokens) {
        return {
          key: 'push-notifications-banner',
          description: 'הפעלת התראות',
          imgName: 'push-notifications.svg',
          onClick: () => this.openPushNotificationPopup(),
        };
      }
      if (this.newAppBanner && isAndroid() && !isNativeApp()) {
        return {
          key: 'new-app-banner',
          description: 'מהיום רייזאפ גם באפליקציה!',
          imgName: 'android_app_banner.svg',
          onClick: () => this.goToNewApp(),
        };
      }
      if (this.newIosAppBanner && isIOS() && !isNativeApp()) {
        return {
          key: 'new-ios-app-banner',
          description: 'מהיום רייזאפ גם באפליקציה!',
          imgName: 'ios_app_banner.svg',
          onClick: () => this.goToNewApp(),
        };
      }
      if (!this.preventPayments) {
        return {
          key: 'ambassadors-menu-banner',
          description: 'לפרגן במינוי בהנחה ולהרוויח על הדרך',
          imgName: 'ambassador_banner.svg',
          onClick: () => this.goToAmbassadors(),
        };
      }

      return undefined;
    },
    showAigMortgageInsurance() {
      return !this.customerHasHeverBenefit && !this.isDormant;
    },
    hasOffersSection() {
      return this.showAigMortgageInsurance
        || !this.preventPayments
        || this.showTaxReturnPage
        || this.showIBIInvestmentsPage
        || this.showExperimentalPages;
    },
    customerStateIsActive() {
      return this.hasAccessToCashflow
          && !this.cashflowOutOfDate
          && !this.cashflowBlocked;
    },
    cashflowOutOfDate() {
      return this.cashflowState === this.cashflowTypes.OUT_OF_DATE;
    },
    cashflowBlocked() {
      return this.cashflowState === this.cashflowTypes.BLOCKED;
    },
    vouchersTarget() {
      if (this.showNewVouchersFlow) {
        return 'super-discount-v2/all';
      }
      return 'super-discount';
    },
  },
  async created() {
    if (this.promotion) {
      Segment.trackUserInteraction(`NavigationMenu_${this.promotion.key}BannerShown`);
    }
    this.customerHasHeverBenefit = await this.hasHeverBenefit();
  },
  mounted() {
    this.initPushNotifications();
    this.$nextTick(() => {
      this.$refs.menu.focus();
    });
  },
  methods: {
    ...mapActions('login', ['logout']),
    ...mapActions('modalRootStore', ['openModal']),
    ...mapActions('planAhead', ['addPlan']),
    ...mapActions('pushNotifications', ['initPushNotifications']),
    ...mapActions('benefits', ['hasHeverBenefit']),
    ...mapMutations('dailyRoutines', ['setShowDailyRoutineViewer']),
    openFaq() {
      Segment.trackUserInteraction('NavigationMenu_HelpCenterClicked');
      window.open('https://intercom.riseup.co.il/he?utm_source=help-center&utm_medium=riseup-menue', '_blank');
      this.closeModal();
    },
    goToFacebook() {
      Segment.trackUserInteraction('NavigationMenu_FacebookGroup');
      window.open('https://www.facebook.com/groups/riseuptogethergroup', '_blank');
      this.closeModal();
    },
    goToAigMortgageInsurance() {
      Segment.trackUserInteraction('NavigationMenu_AigMortgageInsuranceClicked');
      window.open('https://join.aig.co.il/partnership/Mortgage_Riseup', '_blank');
      this.closeModal();
    },
    goToAdvisory() {
      Segment.trackUserInteraction('NavigationMenu_AdvisoryClicked');
      window.open('https://www.riseup.co.il/advisory/', '_blank');
      this.closeModal();
    },
    goToAmbassadors() {
      router.push({ path: '/ambassadors' });
      Segment.trackUserInteraction('NavigationMenu_AmbassadorsClicked_FromImage');
      this.closeModal();
    },
    goToEoy() {
      router.push({ path: '/st/eoy2024' });
      this.closeModal();
    },
    goToNewApp() {
      externalLinkRedirect('https://riseupil.onelink.me/SGmG/p1fqkz64');
      this.closeModal();
    },
    goToAmbassadorsFromNavigationMenu() {
      Segment.trackUserInteraction('NavigationMenu_AmbassadorsClicked');
      this.goToAmbassadors();
    },
    openChat() {
      Segment.trackUserInteraction('NavigationMenu_HelpChatClicked');
      this.closeModal();
      openAssistant({ initiator: pageInitiator(this.$route.name), initialMessage: HELP_CHAT_MESSAGE });
    },
    goToPlanAheadPage() {
      Segment.trackUserInteraction('NavigationMenu_PlanAheadDashboardClicked');
      Segment.trackUserGot(
        'PlanAhead_IntroductionPopup_Test',
        { showIntro: true, hasPlans: this.hasPlans, source: 'NavigationMenu' },
      );
      if (!this.hasPlans) {
        Segment.trackUserGot('PlanAhead_IntroductionPopup_Open', { source: 'NavigationMenu' });
        this.openModal({
          component: PlanAheadIntroductionPopup,
          popupAlignment: 'full-screen',
        });
      }
      this.closeModal();
    },
    openPromotion() {
      Segment.trackUserInteraction('NavigationMenu_PromotionClicked', { promotionKey: this.promotion.key });
      this.promotion.onClick();
    },
    navigate(name) {
      router.push({ name, query: this.$route.query });
    },
    openDailyRoutine() {
      this.setShowDailyRoutineViewer(true);
      this.closeModal();
    },
    openPushNotificationPopup() {
      this.openModal({
        component: PushNotificationPermissionPopup,
        popupAlignment: 'full-screen',
      });
      this.closeModal();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@riseupil/base-ui/src/scss/riseup-colors';
@import '~@riseupil/base-ui/src/scss/spacings';

.nav-bar {
  min-height: 100%;
  background: #FCF7ED;
  box-shadow: 0 4px 2px rgba(0, 0, 0, 0.25);
  border-radius: 24px 0 0 24px;
  padding: 21px $ri-spacing-ml $ri-spacing-l;
  outline: none;
  color: $riseup_dark_green;

  .promotion-banner {
    margin-top: $ri-spacing-l;
    .image-promotion {
      height: 120px;
      img {
        cursor: pointer;
        max-width: 100%;
      }
    }
  }

  .menu-section {
    display: flex;
    flex-direction: column;
    row-gap: 26px;
    padding-right: 13px;

    &.first {
      margin-top: 41px;
    }

    &.compact ::v-deep .menu-item {
      min-height: 26px;
    }
  }

  .divider {
    height: 1px;
    margin: 26px 0 26px 8px;
    background: rgba(32, 33, 37, 0.15);

    &.bottom {
      margin: 30px 0 30px 8px;
    }
  }

  .tiles-grid {
    margin-top: 43px;
    padding-right: 8px;
    display: flex;
    justify-content: space-between;
  }

  .small-circle {
    width: 23px;
    height: 23px;
    border-radius: 50%;
    background-color: $riseup_danger_red;
    color: $riseup_white;
    text-align: center;
    line-height: 23px;
  }
}
</style>
